import { Helmet } from 'react-helmet-async'
import { Header } from '@/components/landing/Header'
import { LandingFooter } from '@/components/landing/LandingFooter'
import { Breadcrumb } from '@/components/Breadcrumb'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useTranslation } from '@/hooks/useTranslation'
import { toAbsoluteUrl } from '@/lib/seo'
import { MARKETING_ROUTES } from '@/lib/marketingRoutes'

const InAppFeedbackWidget = () => {
  const { t, i18n } = useTranslation()
  const isFrench = i18n.language?.startsWith('fr')
  const pagePath = isFrench ? MARKETING_ROUTES.inAppFeedbackWidget.fr : MARKETING_ROUTES.inAppFeedbackWidget.en
  const metaTitle = t('productPage.inAppFeedbackWidget.metaTitle')
  const metaDescription = t('productPage.inAppFeedbackWidget.metaDescription')

  const breadcrumbs = [
    { name: t('productPage.home'), url: isFrench ? MARKETING_ROUTES.home.fr : MARKETING_ROUTES.home.en },
    { name: t('productPage.inAppFeedbackWidget.breadcrumb'), url: pagePath },
  ]

  const faq = [
    {
      question: t('productPage.inAppFeedbackWidget.faq1.question'),
      answer: t('productPage.inAppFeedbackWidget.faq1.answer'),
    },
    {
      question: t('productPage.inAppFeedbackWidget.faq2.question'),
      answer: t('productPage.inAppFeedbackWidget.faq2.answer'),
    },
    {
      question: t('productPage.inAppFeedbackWidget.faq3.question'),
      answer: t('productPage.inAppFeedbackWidget.faq3.answer'),
    },
  ]

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: toAbsoluteUrl(crumb.url),
    })),
  }

  return (
    <div className="fb-public min-h-screen w-full relative overflow-hidden bg-white">
      <a href="#main-content" className="fb-skip-link">{isFrench ? 'Aller au contenu' : 'Skip to content'}</a>

      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={toAbsoluteUrl(pagePath)} />
        <link rel="alternate" hrefLang="en" href={toAbsoluteUrl(MARKETING_ROUTES.inAppFeedbackWidget.en)} />
        <link rel="alternate" hrefLang="fr" href={toAbsoluteUrl(MARKETING_ROUTES.inAppFeedbackWidget.fr)} />
        <link rel="alternate" hrefLang="x-default" href={toAbsoluteUrl(MARKETING_ROUTES.inAppFeedbackWidget.en)} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={toAbsoluteUrl(pagePath)} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <main id="main-content">
      <div className="relative z-10 max-w-7xl px-4 mx-auto">
        <Header />

        <Breadcrumb items={breadcrumbs} />

        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="py-20 md:py-28 grid md:grid-cols-2 gap-10 items-center"
        >
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
              {t('productPage.inAppFeedbackWidget.title')}
            </h1>
            <p className="mt-6 text-gray-600 text-lg max-w-xl">
              {t('productPage.inAppFeedbackWidget.description')}
            </p>
            <div className="mt-8 flex gap-4">
              <Link to="/auth" className="inline-block bg-[#6547D9] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#5638C7] transition">
                {t('productPage.tryFree')}
              </Link>
              <Link to="/docs" className="inline-block bg-white border border-gray-200 text-gray-900 font-semibold px-6 py-3 rounded-full hover:bg-gray-50 transition">
                {t('productPage.installationGuide')}
              </Link>
            </div>
          </div>
          <div className="md:justify-self-end">
            <div className="relative rounded-2xl overflow-hidden flex items-center justify-center min-h-[320px]">
              <div className="absolute inset-0 bg-[#f1ecfe]" />
              <div className="relative z-10 w-full max-w-md mx-auto p-4">
                <img src="/images/interface/widget-min.png" alt="In‑app feedback widget" className="w-full h-auto rounded-lg shadow-xl" onError={(e) => { (e.target as HTMLImageElement).src = '/images/widget-min.png' }} />
              </div>
            </div>
          </div>
        </motion.section>

        <section className="py-8 md:py-12">
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4">{t('productPage.seeItInAction')}</h2>
          <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <video
              src="/images/interface/dashboard.mp4"
              className="w-full h-auto"
              preload="metadata"
              poster="/images/interface/dashboard-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              controls
            />
          </div>
        </section>

        <section className="py-12">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-primary"></span><h3 className="text-xl font-semibold">{t('productPage.inAppFeedbackWidget.feature1.title')}</h3></div>
              <p className="text-gray-600 mt-2">{t('productPage.inAppFeedbackWidget.feature1.description')}</p>
              
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-primary"></span><h3 className="text-xl font-semibold">{t('productPage.inAppFeedbackWidget.feature2.title')}</h3></div>
              <p className="text-gray-600 mt-2">{t('productPage.inAppFeedbackWidget.feature2.description')}</p>
              
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-primary"></span><h3 className="text-xl font-semibold">{t('productPage.inAppFeedbackWidget.feature3.title')}</h3></div>
              <p className="text-gray-600 mt-2">{t('productPage.inAppFeedbackWidget.feature3.description')}</p>
              
            </div>
          </div>
        </section>

        <section className="py-16 text-center bg-[#6547D9] text-white rounded-2xl shadow-lg">
          <h2 className="text-3xl font-bold">{t('productPage.inAppFeedbackWidget.ctaTitle')}</h2>
          <Link to="/auth" className="inline-block mt-6 bg-white text-[#6547D9] font-semibold px-8 py-3 rounded-full hover:bg-gray-100 transition">{t('productPage.getStartedFree')}</Link>
          <div className="mt-6 text-sm opacity-90">
            {t('productPage.related')}: <Link to={isFrench ? MARKETING_ROUTES.websiteFeedbackWidget.fr : MARKETING_ROUTES.websiteFeedbackWidget.en} className="underline text-white">{t('productPage.websiteFeedback')}</Link> · <Link to={isFrench ? MARKETING_ROUTES.featureVotingTool.fr : MARKETING_ROUTES.featureVotingTool.en} className="underline text-white">{t('productPage.featureVoting')}</Link> · <Link to={isFrench ? MARKETING_ROUTES.publicRoadmapSoftware.fr : MARKETING_ROUTES.publicRoadmapSoftware.en} className="underline text-white">{t('productPage.publicRoadmap')}</Link>
          </div>
        </section>

        <section className="py-12">
          <h2 className="text-2xl font-bold mb-6">{t('productPage.faq')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {faq.map((item, idx) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold">{item.question}</h3>
                <p className="text-gray-700 mt-2">{item.answer}</p>
              </div>
            ))}
          </div>
        </section>

        <LandingFooter fr={isFrench} />
      </div>
      </main>
    </div>
  )
}

export default InAppFeedbackWidget 